import * as React from 'react';
import {
  Box,
  Modal,
  Button,
  StackLayout,
  HeadingText,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { FlowCanvas } from '../flow-canvas/flow-canvas.component';
import type { FlowNode, NodeNextKeyMap, NodeTypeLabelMap } from '../../types';
import { FlowProvider } from '../../context/flow-context';

export interface FlowViewerProps {
  data: Array<FlowNode>;
  nodeTypeLabelMap?: NodeTypeLabelMap;
  nodeNextKeyMap?: NodeNextKeyMap;
  addStartNode?: boolean;
  addEndNode?: boolean;
}

export const FlowViewer = ({
  data,
  nodeTypeLabelMap,
  nodeNextKeyMap,
  addStartNode,
  addEndNode,
}: FlowViewerProps) => {
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  return (
    <FlowProvider
      nodeTypeLabelMap={nodeTypeLabelMap}
      nodeNextKeyMap={nodeNextKeyMap}
      addStartNode={addStartNode}
      addEndNode={addEndNode}
    >
      <StackLayout
        css={{ width: '100%' }}
        orientation="horizontal"
        horizontalAlignment="end"
        data-testid="flow-viewer"
        data-node-next-key-map={JSON.stringify(
          nodeNextKeyMap || { action: 'next', decision: 'next' },
        )}
        data-add-start={Boolean(addStartNode)}
        data-add-end={Boolean(addEndNode)}
      >
        <Button
          onClick={() => setIsOpen(true)}
          size="toolbar"
          priority="secondary"
          icon="decisionNodeIcon"
          css={{
            '.GDS-icon': {
              display: 'inline-flex',
              alignItems: 'flex-end',
            },
          }}
        >
          {formatMessage({ id: 'VIEW_GRAPH' })}
        </Button>
      </StackLayout>

      <Modal.Root open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
        <Modal.Content
          layout="fullscreen"
          css={{
            '.GDS-modal-content': { height: '90vh' },
          }}
        >
          <Modal.Title>
            <HeadingText level="1" size="xxl">
              {formatMessage({ id: 'FLOW_GRAPH' })}
            </HeadingText>
          </Modal.Title>
          <Box css={{ padding: '$two', height: '100%' }}>
            <FlowCanvas data={data} />
          </Box>
        </Modal.Content>
      </Modal.Root>
    </FlowProvider>
  );
};
